(() => {
  'use strict';

  const config = window.KSSLeadConfig || {};

  if (!config.endpoint || !config.turnstileSiteKey) {
    console.warn('KSS Lead Forms: endpoint or Turnstile Site Key is missing.');
    return;
  }

  const LEAD_ENDPOINT_PATH = '/v1/leads';
  const TURNSTILE_ACTION = "lead_submit";
  const MAX_TURNSTILE_LOAD_ATTEMPTS = 100;
  const TURNSTILE_RETRY_DELAY_MS = 100;
  const SUCCESS_PARAM = 'ks_lead_success';
  const SUCCESS_STORAGE_KEY = 'kssLeadSuccess';
  const SUCCESS_MAX_AGE_MS = 5 * 60 * 1000;

  const PAGE_VALUES = new Map([
    ['home', 'Home'],
    ['homepage', 'Home'],
    ['sales', 'Sales'],
    ['sale', 'Sales'],
    ['sukkah sales', 'Sales'],
    ['buying a sukkah', 'Sales'],
    ['rental', 'Rental'],
    ['rentals', 'Rental'],
    ['sukkah rental', 'Rental'],
    ['contact', 'Contact'],
    ['general contact', 'Contact'],
    ['locations', 'Locations'],
    ['service areas', 'Locations'],
    ['projects', 'Projects'],
    ['project gallery', 'Projects'],
    ['sukkah sales campaign', 'Sukkah Sales Campaign'],
    ['full-service rental campaign', 'Full-Service Rental Campaign']
  ]);

  const leadTypeMap = {
    'Buying a Sukkah': 'Sales',
    'Sukkah Rental': 'Rental',
    'Commercial or Community Project': 'Commercial',
    'Rooftop or Space-Sensitive Project': 'Commercial',
    'General Question': 'General',
    'Not Sure Yet': 'General'
  };

  let turnstileLoadAttempts = 0;
  let turnstileLoadTimer = null;

  const normalizePage = (rawValue) => {
    const normalized = String(rawValue || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

    return PAGE_VALUES.get(normalized) || '';
  };

  const pageFromUrl = () => {
    const path = window.location.pathname
      .toLowerCase()
      .replace(/\/+$/, '') || '/';

    if (path === '/') return 'Home';

    if (/full-service.*rental|rental.*campaign/.test(path)) {
      return 'Full-Service Rental Campaign';
    }

    if (/sukkah.*sales.*campaign|sales.*campaign/.test(path)) {
      return 'Sukkah Sales Campaign';
    }

    if (/sukkah-for-sale|sukkah-sales|\/sales(?:\/|$)|\/buy(?:\/|$)/.test(path)) {
      return 'Sales';
    }

    if (/sukkah-rental|sukkah-rentals|\/rentals?(?:\/|$)/.test(path)) {
      return 'Rental';
    }

    if (/contact/.test(path)) return 'Contact';
    if (/location|service-area/.test(path)) return 'Locations';
    if (/project/.test(path)) return 'Projects';

    return '';
  };

  const resolveSourcePage = (form) => {
    const declaredPage = normalizePage(
      value(form, 'contact[Source Page]')
    );

    if (declaredPage) return declaredPage;

    const pageContext = normalizePage(
      value(form, 'contact[Page Context]')
    );

    if (pageContext) return pageContext;

    return pageFromUrl() || 'General';
  };

  const endpointUrl = () => {
    const rawEndpoint = String(config.endpoint || '').trim();

    if (!rawEndpoint) return '';

    try {
      const url = new URL(rawEndpoint, window.location.origin);
      const cleanPath = url.pathname.replace(/\/+$/, '');

      if (!cleanPath || cleanPath === '/') {
        url.pathname = LEAD_ENDPOINT_PATH;
      }

      return url.toString();
    } catch {
      return rawEndpoint;
    }
  };

  const field = (form, name) => {
    return form.elements.namedItem(name);
  };

  const value = (form, name) => {
    const input = field(form, name);

    return typeof input?.value === 'string'
      ? input.value.trim()
      : '';
  };

  const setValue = (form, name, nextValue) => {
    const input = field(form, name);

    if (input && 'value' in input) {
      input.value = nextValue ?? '';
    }
  };

  const phoneDigits = (inputValue) => {
    let digits = String(inputValue || '').replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    return digits.slice(0, 10);
  };

  const formatPhone = (inputValue) => {
    const digits = phoneDigits(inputValue);

    if (!digits) return '';
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const isValidUsPhone = (inputValue) => {
    const digits = phoneDigits(inputValue);

    return (
      digits.length === 10 &&
      /^[2-9]/.test(digits) &&
      /^[2-9]/.test(digits.slice(3))
    );
  };

  const e164Phone = (inputValue) => {
    return isValidUsPhone(inputValue)
      ? `+1${phoneDigits(inputValue)}`
      : '';
  };

  const initializePhone = (form) => {
    const input = form.querySelector('[data-ks-phone]');

    if (!input || input.dataset.ksPhoneReady === 'true') {
      return;
    }

    input.dataset.ksPhoneReady = 'true';
    input.value = formatPhone(input.value);

    const validate = () => {
      const hasValue = phoneDigits(input.value).length > 0;

      input.setCustomValidity(
        !hasValue || isValidUsPhone(input.value)
          ? ''
          : 'Enter a valid 10-digit U.S. phone number.'
      );
    };

    input.addEventListener('input', () => {
      input.value = formatPhone(input.value);
      validate();
    });

    input.addEventListener('blur', validate);
  };

  const successUrl = (form) => {
    const url = new URL(window.location.href);

    url.searchParams.set(SUCCESS_PARAM, form.id);
    url.hash = form.querySelector('[data-ks-lead-success]')?.id || form.id;

    return url.toString();
  };

  const rememberSuccess = (form) => {
    const marker = {
      formId: form.id,
      path: window.location.pathname,
      createdAt: Date.now()
    };

    try {
      window.sessionStorage.setItem(
        SUCCESS_STORAGE_KEY,
        JSON.stringify(marker)
      );
    } catch (error) {
      console.warn(
        'KSS Lead Forms: could not persist the success state.',
        error
      );
    }
  };

  const restoreSuccess = (form) => {
    const params = new URLSearchParams(window.location.search);

    if (params.get(SUCCESS_PARAM) !== form.id) {
      return false;
    }

    let marker = null;

    try {
      marker = JSON.parse(
        window.sessionStorage.getItem(SUCCESS_STORAGE_KEY) || 'null'
      );
    } catch {
      marker = null;
    }

    const isValidMarker =
      marker?.formId === form.id &&
      marker?.path === window.location.pathname &&
      Date.now() - Number(marker?.createdAt || 0) <= SUCCESS_MAX_AGE_MS;

    try {
      window.sessionStorage.removeItem(SUCCESS_STORAGE_KEY);
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }

    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete(SUCCESS_PARAM);
    window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);

    if (!isValidMarker) {
      return false;
    }

    const success = form.querySelector('[data-ks-lead-success]');

    if (!success) {
      return false;
    }

    Array.from(form.children).forEach((child) => {
      child.hidden = child !== success;
    });

    success.hidden = false;
    form.dataset.ksLeadComplete = 'true';

    window.requestAnimationFrame(() => {
      success.focus({ preventScroll: true });
      success.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'center'
      });
    });

    return true;
  };

  const submissionId = () => {
    const randomPart = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

    return `KSS-${randomPart}`;
  };

  const showStatus = (form, message, state) => {
    const status = form.querySelector('[data-ks-form-status]');

    if (!status) return;

    status.hidden = false;
    status.dataset.state = state;
    status.textContent = message;

    status.setAttribute(
      'role',
      state === 'error' ? 'alert' : 'status'
    );

    status.setAttribute(
      'aria-live',
      state === 'error' ? 'assertive' : 'polite'
    );

    status.setAttribute('tabindex', '-1');
    status.focus({ preventScroll: true });
  };

  const clearStatus = (form) => {
    const status = form.querySelector('[data-ks-form-status]');

    if (!status) return;

    status.hidden = true;
    status.removeAttribute('data-state');
    status.textContent = '';
  };

  const getWidgetId = (container) => {
    if (
      !container ||
      !Object.prototype.hasOwnProperty.call(
        container.dataset,
        'widgetId'
      )
    ) {
      return null;
    }

    return container.dataset.widgetId;
  };

  const renderTurnstile = (form) => {
    const container = form.querySelector('[data-ks-turnstile]');

    if (
      !container ||
      form.dataset.ksLeadComplete === 'true' ||
      getWidgetId(container) !== null ||
      !window.turnstile
    ) {
      return;
    }

    try {
      const widgetId = window.turnstile.render(container, {
        sitekey: config.turnstileSiteKey,
        theme: 'auto',
        size: 'flexible',
        action: TURNSTILE_ACTION,
        retry: 'auto',
        'refresh-expired': 'auto',

        callback: () => {
          clearStatus(form);
        },

        'expired-callback': () => {
          showStatus(
            form,
            'The security verification expired. Please complete it again.',
            'error'
          );
        },

        'error-callback': (code) => {
          console.error(
            'KSS Lead Forms: Turnstile error.',
            code
          );

          showStatus(
            form,
            `Security verification could not load. Please refresh the page and try again. (${code || 'unknown'})`,
            'error'
          );

          return true;
        }
      });

      container.dataset.widgetId = String(widgetId);
    } catch (error) {
      console.error(
        'KSS Lead Forms: Turnstile rendering failed.',
        error
      );

      showStatus(
        form,
        'Security verification could not load. Please refresh the page and try again.',
        'error'
      );
    }
  };

  const resetTurnstile = (form) => {
    const container = form.querySelector('[data-ks-turnstile]');
    const widgetId = getWidgetId(container);

    if (widgetId !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetId);
      } catch (error) {
        console.error(
          'KSS Lead Forms: Turnstile reset failed.',
          error
        );
      }
    }
  };

  const turnstileToken = (form) => {
    const container = form.querySelector('[data-ks-turnstile]');
    const widgetId = getWidgetId(container);

    if (widgetId === null || !window.turnstile) {
      return '';
    }

    try {
      return window.turnstile.getResponse(widgetId) || '';
    } catch (error) {
      console.error(
        'KSS Lead Forms: could not read Turnstile response.',
        error
      );

      return '';
    }
  };

  const initialize = (form) => {
    if (form.dataset.ksLeadReady === 'true') {
      return;
    }

    form.dataset.ksLeadReady = 'true';

    if (restoreSuccess(form)) {
      return;
    }

    initializePhone(form);

    const inquiry = field(form, 'contact[Inquiry type]');
    const params = new URLSearchParams(window.location.search);
    const page = resolveSourcePage(form);

    const updateMetadata = () => {
      const leadType =
        leadTypeMap[inquiry?.value || ''] ||
        'General';

      const id =
        value(form, 'contact[Submission ID]') ||
        submissionId();

      setValue(form, 'contact[Lead Type]', leadType);
      setValue(form, 'contact[Page Context]', page);
      setValue(form, 'contact[Source Page]', page);
      setValue(form, 'contact[Submission ID]', id);

      setValue(
        form,
        'contact[Submitted At]',
        new Date().toISOString()
      );

      setValue(
        form,
        'contact[Landing Page]',
        window.location.href
      );

      setValue(
        form,
        'contact[Referrer]',
        document.referrer || ''
      );

      setValue(
        form,
        'contact[Privacy Version]',
        config.privacyVersion || '2026-07-29'
      );

      setValue(
        form,
        'contact[tags]',
        `website-lead, source-${page.toLowerCase()}, lead-${leadType.toLowerCase()}`
      );

      [
        'source',
        'medium',
        'campaign',
        'term',
        'content'
      ].forEach((key) => {
        setValue(
          form,
          `contact[UTM ${key}]`,
          params.get(`utm_${key}`) || ''
        );
      });

      return {
        id,
        leadType,
        page
      };
    };

    inquiry?.addEventListener(
      'change',
      updateMetadata
    );

    updateMetadata();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (form.dataset.ksLeadSubmitting === 'true') {
        return;
      }

      clearStatus(form);

      if (!form.reportValidity()) {
        return;
      }

      const phone = e164Phone(value(form, 'contact[phone]'));

      if (!phone) {
        const phoneInput = form.querySelector('[data-ks-phone]');

        phoneInput?.setCustomValidity(
          'Enter a valid 10-digit U.S. phone number.'
        );
        phoneInput?.reportValidity();
        phoneInput?.focus();
        return;
      }

      const token = turnstileToken(form);

      if (!token) {
        showStatus(
          form,
          'Please complete the security verification and try again.',
          'error'
        );

        return;
      }

      const meta = updateMetadata();
      form.dataset.ksLeadSubmitting = 'true';

      const submit = form.querySelector(
        'button[type="submit"], input[type="submit"]'
      );

      const submitLabel =
        submit?.tagName === 'BUTTON'
          ? submit.querySelector('span')
          : null;

      const originalButtonText =
        submitLabel
          ? submitLabel.textContent
          : submit?.value;

      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');

        if (submitLabel) {
          submitLabel.textContent = 'Sending…';
        } else {
          submit.value = 'Sending…';
        }
      }

      const payload = {
        name: value(form, 'contact[name]'),
        phone,
        email: value(form, 'contact[email]'),

        inquiryType: value(
          form,
          'contact[Inquiry type]'
        ),

        leadType: meta.leadType,
        leadSource: 'Website',
        pageContext: meta.page,
        sourcePage: meta.page,

        cityOrZip: value(
          form,
          'contact[City or ZIP Code]'
        ),

        message: value(form, 'contact[body]'),
        tags: value(form, 'contact[tags]'),

        submissionId: meta.id,

        submittedAt: value(
          form,
          'contact[Submitted At]'
        ),

        landingPage: value(
          form,
          'contact[Landing Page]'
        ),

        referrer: value(
          form,
          'contact[Referrer]'
        ),

        privacyConsent:
          field(
            form,
            'contact[Privacy Consent]'
          )?.checked === true,

        privacyVersion:
          config.privacyVersion || '2026-07-29',

        utm: {
          source: value(
            form,
            'contact[UTM source]'
          ),

          medium: value(
            form,
            'contact[UTM medium]'
          ),

          campaign: value(
            form,
            'contact[UTM campaign]'
          ),

          term: value(
            form,
            'contact[UTM term]'
          ),

          content: value(
            form,
            'contact[UTM content]'
          )
        },

        turnstileToken: token,
        website: value(form, 'website')
      };

      try {
        const response = await fetch(endpointUrl(), {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',

          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },

          body: JSON.stringify(payload)
        });

        const result = await response
          .json()
          .catch(() => ({}));

        if (!response.ok || result.ok !== true) {
          const error = new Error(
            result.code ||
            `http_${response.status}`
          );

          error.response = result;
          throw error;
        }
                gtag_report_conversion(window.location);
                rememberSuccess(form);

                if (typeof window.fbq === 'function') {
                  window.fbq(
                    'track',
                    'Lead',
                    {
                      content_name: meta.page,
                      content_category: meta.leadType,
                      inquiry_type: payload.inquiryType
                    },
                    {
                      eventID: meta.id
                    }
                  );
                } else {
                  console.warn(
                    'KSS Lead Forms: Meta Pixel was unavailable when the lead succeeded.'
                  );
                }

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', {
            lead_type: meta.leadType,
            source_page: meta.page,
            inquiry_type: payload.inquiryType,
            submission_id: meta.id,
            lead_source: 'Website'
          });
        } else {
          console.warn(
            'KSS Lead Forms: Google tag was unavailable when the lead succeeded.'
          );
        }

        window.setTimeout(() => {
          window.location.assign(successUrl(form));
        }, 300);

        return;
      } catch (error) {
        console.error(
          'KSS Lead Forms: submission failed.',
          error
        );

        showStatus(
          form,
          'We could not send your request. Please try again or call (929) 595-7424.',
          'error'
        );
      } finally {
        delete form.dataset.ksLeadSubmitting;

        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');

          if (submitLabel) {
            submitLabel.textContent = originalButtonText;
          } else {
            submit.value = originalButtonText;
          }
        }

        resetTurnstile(form);
      }
    }, { capture: true });
  };

  const start = () => {
    const forms = document.querySelectorAll(
      'form[data-ks-lead-form="true"]'
    );

    forms.forEach(initialize);

    if (window.turnstile) {
      turnstileLoadAttempts = 0;

      if (turnstileLoadTimer) {
        window.clearTimeout(turnstileLoadTimer);
        turnstileLoadTimer = null;
      }

      forms.forEach(renderTurnstile);
      return;
    }

    if (!forms.length || turnstileLoadTimer) {
      return;
    }

    turnstileLoadAttempts += 1;

    if (
      turnstileLoadAttempts >=
      MAX_TURNSTILE_LOAD_ATTEMPTS
    ) {
      console.error(
        'KSS Lead Forms: Turnstile API did not load.'
      );

      forms.forEach((form) => {
        showStatus(
          form,
          'Security verification could not load. Please refresh the page and try again.',
          'error'
        );
      });

      return;
    }

    turnstileLoadTimer = window.setTimeout(() => {
      turnstileLoadTimer = null;
      start();
    }, TURNSTILE_RETRY_DELAY_MS);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    start
  );
})();
